/**
 * The AI has one reply mode for while the business is open and one for while it is closed.
 * The server works out which period applies (from the business hours) and returns it as
 * `replyStatus` with the settings, so the client never evaluates business hours itself.
 */
export const REPLY_MODES = ['auto', 'delayed', 'assist', 'off'] as const;
export type ReplyMode = (typeof REPLY_MODES)[number];
export type ReplyPeriod = 'open' | 'closed' | 'unknown';

/** Returned next to `ai` by GET and PUT /api/ai-employee/settings/:business. */
export interface ReplyStatus {
  period?: ReplyPeriod;
  currentMode?: ReplyMode;
  hoursSet?: boolean;
}

export interface ReplyModeSettings {
  mode?: string;
  operatingHoursMode?: string;
  openHoursMode?: string;
  closedHoursMode?: string;
}

export interface ReplyModePair {
  openHoursMode: ReplyMode;
  closedHoursMode: ReplyMode;
}

export const isReplyMode = (value: unknown): value is ReplyMode =>
  REPLY_MODES.includes(value as ReplyMode);

/**
 * The open and closed hours modes of a settings `ai` object. Older servers only return the single
 * legacy `mode` + `operatingHoursMode`, so those are mapped the same way the server does
 * (ai-employee src/lib/reply-modes.ts replyModesFor).
 */
export function replyModesFrom(ai?: ReplyModeSettings | null): ReplyModePair {
  const { mode, operatingHoursMode, openHoursMode, closedHoursMode } = ai ?? {};
  const legacy = isReplyMode(mode) ? mode : 'off';
  return {
    openHoursMode: isReplyMode(openHoursMode)
      ? openHoursMode
      : operatingHoursMode === 'after_hours'
        ? 'off'
        : legacy,
    closedHoursMode: isReplyMode(closedHoursMode)
      ? closedHoursMode
      : operatingHoursMode === 'during_hours'
        ? 'off'
        : legacy,
  };
}

/** True when the AI replies in at least one of the two periods. */
export function isAiReplyOn(ai?: ReplyModeSettings | null): boolean {
  const { openHoursMode, closedHoursMode } = replyModesFrom(ai);
  return openHoursMode !== 'off' || closedHoursMode !== 'off';
}

/** The mode in effect right now: the server's `replyStatus`, or the legacy mode on older servers. */
export function currentReplyMode(
  ai?: ReplyModeSettings | null,
  replyStatus?: ReplyStatus | null,
): ReplyMode {
  const current = replyStatus?.currentMode;
  if (isReplyMode(current)) return current;
  const legacy = ai?.mode;
  return isReplyMode(legacy) ? legacy : 'off';
}
